import React, {FC, useEffect, useState} from "react"
import PropTypes from "prop-types"
import moment from "moment"

import {H1, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {FormattedMessage} from "@irati/utils/lang"
import salesService from "@irati/sales/services/sales"
import buService from "@irati/billing/services/bu"
import authentication from "@irati/auth"
import {RenewalQuoteStatus, ZuoraCurrentPlanWindow} from "@irati/types"
import Button from "@irati/ui/src/Button"
import adminTracking from "@irati-tracking/admin"

import {useTheme} from "@irati/ui/src/global/useTheme"
import History from "@irati/utils/history"
import {RenewalPlanSource} from "@irati-tracking/admin/admin"
import {BILLING_TABS} from "../../containers/Billing"
import {StyledDiv} from "./styled"

interface Props {}

interface Context {
  apps: {[key: string]: string}
}

const RenewalsReminderBanner: FC<Props> = (_, context: Context) => {
  const {apps} = context
  const [showReminderBanner, setShowReminderBanner] = useState(false)
  const [showUpcomingRenewalBanner, setShowUpcomingRenewalBanner] = useState(false)
  const [showEvergreenRenewalBanner, setShowEvergreenRenewalBanner] = useState(false)
  const [evergreenBackStopDate, setEvergreenBackStopDate] = useState("")
  const [renewalDate, setRenewalDate] = useState("")
  const theme = useTheme()

  useEffect(() => {
    if (authentication.isGlobalAdmin()) {
      const businessId = authentication.business().uuid

      Promise.all([buService.getCurrentPlan({businessId}), salesService.getRenewalQuoteStatus(businessId)]).then(
        ([{endDate, window, renewalPending}, quote]) => {
          const isRenewalWindowOpen =
            window === ZuoraCurrentPlanWindow.RenewalWindow ||
            window === ZuoraCurrentPlanWindow.RenewalWindowWithoutCancellation ||
            (window === ZuoraCurrentPlanWindow.Ongoing && renewalPending) ||
            false
          if (isRenewalWindowOpen) {
            if (quote?.sentForSignature?.seconds) {
              const is6HoursAfterSentForSignature = moment().isAfter(moment.unix(quote.sentForSignature.seconds).add(6, "hours"))
              if (quote.status === RenewalQuoteStatus.SentForSignature && is6HoursAfterSentForSignature) {
                setShowReminderBanner(true)
                setRenewalDate(moment(endDate).format("DD/MM/YYYY"))
              }
            } else if (quote?.status === RenewalQuoteStatus.Available && !quote.isManual) {
              setRenewalDate(moment(endDate).format("DD/MM/YYYY"))
              if (quote?.isEvergreen) {
                setShowEvergreenRenewalBanner(true)
                setEvergreenBackStopDate(moment.unix(quote.evergreenBackstopDate.seconds).format("DD/MM/YYYY"))
              } else {
                setShowUpcomingRenewalBanner(true)
              }
            }
          }
        }
      )
    }
  }, [])

  const handleUpcomingRenewalBannerClick = () => {
    adminTracking.renewalPlan({
      action: RenewalPlanSource.reviewRenewalPlan
    })
    History.push({pathname: `/billing/${BILLING_TABS.NEW_PLAN}`, state: {source: RenewalPlanSource.reviewRenewalPlan}}, apps.ADMIN)
  }

  const handleReminderBannerClick = () => {
    adminTracking.renewalPlan({
      action: RenewalPlanSource.reviewRenewalStatus
    })
    History.push({pathname: `/billing/${BILLING_TABS.RENEWAL_STATUS}`, state: {source: RenewalPlanSource.reviewRenewalStatus}}, apps.ADMIN)
  }

  if (showEvergreenRenewalBanner) {
    return (
      <StyledDiv bgColor={theme.colors.utility.warning}>
        <H1 color="white">
          <FormattedMessage id="platform.renewals.evergreenRenewalBanner.title" />
        </H1>
        <Flex alignItems="center" justifyContent="space-between">
          <Body color="white" maxWidth="none">
            <FormattedMessage
              id="platform.renewals.evergreenRenewalBanner.text"
              values={{
                renewalDate,
                evergreenBackStopDate,
                bold: (
                  <Body color="white" weight="bold" display="inline">
                    <FormattedMessage id="platform.renewals.evergreenRenewalBanner.bold" />
                  </Body>
                )
              }}
            />
          </Body>
          <Button marginLeft={2} inverted onClick={handleUpcomingRenewalBannerClick}>
            <FormattedMessage id="platform.renewals.upcomingRenewal.button" />
          </Button>
        </Flex>
      </StyledDiv>
    )
  }

  if (showUpcomingRenewalBanner) {
    return (
      <StyledDiv bgColor={theme.colors.utility.warning}>
        <H1 color="white">
          <FormattedMessage id="platform.renewals.upcomingRenewalBanner.title" />
        </H1>
        <Flex alignItems="center" justifyContent="space-between">
          <Body color="white" maxWidth="none">
            <FormattedMessage
              id="platform.renewals.upcomingRenewalBanner.text"
              values={{
                bold: (
                  <Body color="white" weight="bold" display="inline">
                    {renewalDate}
                  </Body>
                )
              }}
            />
          </Body>
          <Button marginLeft={2} inverted onClick={handleUpcomingRenewalBannerClick}>
            <FormattedMessage id="platform.renewals.upcomingRenewal.button" />
          </Button>
        </Flex>
      </StyledDiv>
    )
  }

  if (showReminderBanner) {
    return (
      <StyledDiv bgColor={theme.colors.tertiary.r_400}>
        <H1 color="white">
          <FormattedMessage id="platform.renewals.reminderBanner.title" />
        </H1>
        <Flex alignItems="center">
          <Body color="white" maxWidth="none">
            <FormattedMessage id="platform.renewals.reminderBanner.text" values={{renewalDate}} />
          </Body>
          <Button marginLeft={2} inverted onClick={handleReminderBannerClick}>
            <FormattedMessage id="platform.renewals.reminderBanner.button" />
          </Button>
        </Flex>
      </StyledDiv>
    )
  }
  return null
}

RenewalsReminderBanner.contextTypes = {
  apps: PropTypes.object.isRequired,
  appPath: PropTypes.string
}

export default RenewalsReminderBanner
